import { useCallback, useMemo } from "react";
import type { Identifier } from "ra-core";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetMany,
} from "ra-core";

import {
  contactFullName,
  firstPhoneNumber,
  initialsFromName,
  type HoyContact,
} from "../dashboard/hoyPlan";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type {
  AlertRule,
  AlertRuleType,
  Deal,
  DealStage,
  LeadAlert,
} from "../types";
import { findDealLabel } from "../deals/dealUtils";
import type { CrmDataProvider } from "../providers/types";
import { buildAlertMessage } from "./alertMessage";

/**
 * Almikia Epic ER (TASK-007): the data-fetching layer behind both alert
 * surfaces (Hoy's alert cards, the lead page's alert pills). Reads
 * `lead_alerts`/`alert_rules` the same way every other Almikia hook reads a
 * small catalogue -- through `useGetList`, so RLS (TASK-003) is the only
 * place visibility is enforced; this file never widens it (NFR: security).
 * See adr/ADR-ee2f59a9-TASK-007-alert-surfaces.md.
 */

const ALERT_RULES_PARAMS = {
  pagination: { page: 1, perPage: 10 },
  sort: { field: "id", order: "ASC" as const },
  filter: {},
};

/**
 * `AlertRule` keyed by id -- the small, always-cached 4-row catalogue.
 * Exported for TASK-008's Direccion alerts section, which reads the same
 * catalogue for its own rule-grouped view.
 */
export function useAlertRulesById(): Map<Identifier, AlertRule> {
  const { data: alertRules } = useGetList<AlertRule>(
    "alert_rules",
    ALERT_RULES_PARAMS,
  );
  return useMemo(
    () => new Map((alertRules ?? []).map((rule) => [rule.id, rule])),
    [alertRules],
  );
}

/** One resolved alert, ready to render -- the sentence is already built. */
export interface LeadAlertMessage {
  id: Identifier;
  ruleType: AlertRuleType;
  message: string;
}

/**
 * The threshold (hours) past which this lead qualified for this rule --
 * `alert_rules.hours_threshold` for the two "time since last touch" rules,
 * the lead's CURRENT stage's own `target_hours` for `detenido_en_etapa`
 * (`stuck_lead_count_threshold`/`target_stage_key` carry no comparable
 * duration, so `cumple_criterio_sin_cita` has none -- its sentence never
 * renders an elapsed duration, see `alertMessage.ts`). See
 * `AlertMessageInput.thresholdHours` for why this must be added back to
 * `now - raised_at` rather than reading `raised_at` alone. Exported for
 * TASK-008's Direccion alerts section (same reuse rationale as
 * {@link useAlertRulesById}).
 */
export function resolveThresholdHours(
  rule: AlertRule,
  stage: string,
  dealStages: DealStage[],
): number | undefined {
  switch (rule.rule_type) {
    case "sin_seguimiento":
    case "alta_intencion_sin_actividad":
      return rule.hours_threshold ?? undefined;
    case "detenido_en_etapa":
      return (
        dealStages.find((dealStage) => dealStage.value === stage)
          ?.targetHours ?? undefined
      );
    default:
      return undefined;
  }
}

function resolveAlertMessage(
  alert: LeadAlert,
  rule: AlertRule | undefined,
  deal: Pick<Deal, "stage"> | undefined,
  dealStages: ReturnType<typeof useConfigurationContext>["dealStages"],
  now: Date,
): LeadAlertMessage | null {
  if (!rule) return null;
  const message = buildAlertMessage({
    ruleType: rule.rule_type,
    raisedAt: alert.raised_at,
    now,
    thresholdHours: deal
      ? resolveThresholdHours(rule, deal.stage, dealStages)
      : undefined,
    currentStageLabel: deal ? findDealLabel(dealStages, deal.stage) : undefined,
    targetStageLabel: rule.target_stage_key
      ? findDealLabel(dealStages, rule.target_stage_key)
      : undefined,
  });
  if (!message) return null;
  return { id: alert.id, ruleType: rule.rule_type, message };
}

/**
 * The lead page's own open alerts (acceptance criterion 4) -- `stage` is
 * passed in from the already-loaded `Deal` record (`DealShow.tsx`), never
 * refetched, so mounting this on a lead page adds exactly one request.
 */
export function useDealLeadAlerts(deal: { id: Identifier; stage: string }): {
  messages: LeadAlertMessage[];
  isPending: boolean;
  error: unknown;
} {
  const { dealStages } = useConfigurationContext();
  const rulesById = useAlertRulesById();
  const {
    data: alerts,
    isPending,
    error,
  } = useGetList<LeadAlert>("lead_alerts", {
    pagination: { page: 1, perPage: 20 },
    sort: { field: "raised_at", order: "ASC" },
    filter: { deal_id: deal.id, "resolved_at@is": null },
  });

  const messages = useMemo(() => {
    const now = new Date();
    return (alerts ?? [])
      .map((alert) =>
        resolveAlertMessage(
          alert,
          rulesById.get(alert.rule_id),
          deal,
          dealStages,
          now,
        ),
      )
      .filter((message): message is LeadAlertMessage => message != null);
  }, [alerts, rulesById, deal, dealStages]);

  return { messages, isPending, error };
}

const MAX_OWN_OPEN_DEALS = 500;
const MAX_ADVISOR_ALERTS = 200;

/** One of the advisor's own open alerts, resolved into a Hoy-card-shaped record. */
export interface LeadAlertCard {
  id: Identifier;
  dealId: Identifier;
  ruleType: AlertRuleType;
  /** The rule catalogue's own short Spanish label (e.g. "Sin seguimiento") -- the urgency chip's text. */
  label: string;
  message: string;
  name: string;
  initials: string;
  phone: string | null;
  contactId: Identifier | null;
  contact: HoyContact | null;
}

/**
 * The signed-in advisor's own open lead alerts, across every stage (NOT just
 * Hoy's own Prospecto/Discovery scope -- an alert can fire on a lead in any
 * open stage). `evaluateAndRefresh` runs the TASK-005 engine then refetches,
 * satisfying acceptance criterion 3 (on load, and after logging an activity).
 */
export function useAdvisorLeadAlerts(): {
  cards: LeadAlertCard[];
  isPending: boolean;
  error: unknown;
  refetchAlerts: () => void;
  evaluateAndRefresh: () => Promise<void>;
} {
  const { identity } = useGetIdentity();
  const salesId = identity?.id;
  const hasIdentity = Number.isInteger(salesId);
  const { dealStages } = useConfigurationContext();
  const rulesById = useAlertRulesById();
  const dataProvider = useDataProvider<CrmDataProvider>();

  const { data: ownDeals, isPending: isDealsPending } = useGetList<Deal>(
    "deals",
    {
      pagination: { page: 1, perPage: MAX_OWN_OPEN_DEALS },
      sort: { field: "id", order: "ASC" },
      filter: { sales_id: salesId, "archived_at@is": null },
    },
    { enabled: hasIdentity },
  );
  const dealsById = useMemo(
    () => new Map((ownDeals ?? []).map((deal) => [deal.id, deal])),
    [ownDeals],
  );
  const dealIds = useMemo(
    () => (ownDeals ?? []).map((deal) => deal.id),
    [ownDeals],
  );

  const {
    data: alerts,
    isPending: isAlertsPending,
    error,
    refetch: refetchAlerts,
  } = useGetList<LeadAlert>(
    "lead_alerts",
    {
      pagination: { page: 1, perPage: MAX_ADVISOR_ALERTS },
      sort: { field: "raised_at", order: "ASC" },
      filter: {
        "deal_id@in": `(${dealIds.join(",")})`,
        "resolved_at@is": null,
      },
    },
    { enabled: hasIdentity && dealIds.length > 0 },
  );

  const contactIds = useMemo(() => {
    const ids = new Set<Identifier>();
    for (const alert of alerts ?? []) {
      const deal = dealsById.get(alert.deal_id);
      if (deal?.contact_ids?.[0] != null) ids.add(deal.contact_ids[0]);
    }
    return Array.from(ids);
  }, [alerts, dealsById]);
  const { data: contacts } = useGetMany<HoyContact>(
    "contacts",
    { ids: contactIds },
    { enabled: contactIds.length > 0 },
  );
  const contactsById = useMemo(
    () => new Map((contacts ?? []).map((contact) => [contact.id, contact])),
    [contacts],
  );

  const cards = useMemo(() => {
    const now = new Date();
    const result: LeadAlertCard[] = [];
    for (const alert of alerts ?? []) {
      const rule = rulesById.get(alert.rule_id);
      const deal = dealsById.get(alert.deal_id);
      if (!rule || !deal) continue;
      const message = buildAlertMessage({
        ruleType: rule.rule_type,
        raisedAt: alert.raised_at,
        now,
        thresholdHours: resolveThresholdHours(rule, deal.stage, dealStages),
        currentStageLabel: findDealLabel(dealStages, deal.stage),
        targetStageLabel: rule.target_stage_key
          ? findDealLabel(dealStages, rule.target_stage_key)
          : undefined,
      });
      if (!message) continue;
      const contactId = deal.contact_ids?.[0] ?? null;
      const contact =
        contactId != null ? (contactsById.get(contactId) ?? null) : null;
      result.push({
        id: alert.id,
        dealId: deal.id,
        ruleType: rule.rule_type,
        label: rule.label,
        message,
        name: contactFullName(contact ?? undefined),
        initials: initialsFromName(contactFullName(contact ?? undefined)),
        phone: firstPhoneNumber(contact ?? undefined),
        contactId,
        contact,
      });
    }
    return result;
  }, [alerts, rulesById, dealsById, contactsById, dealStages]);

  const evaluateAndRefresh = useCallback(async () => {
    try {
      await dataProvider.evaluateLeadAlerts();
    } catch {
      // Non-blocking: a throttled/failed evaluation just means this refetch
      // reads whatever `lead_alerts` already has -- never a hard error on
      // Hoy (NFR: an alert refresh is a background convenience, not a gate).
    }
    refetchAlerts();
  }, [dataProvider, refetchAlerts]);

  return {
    cards,
    // `isAlertsPending` only reflects a real in-flight request once the
    // query is actually enabled (`dealIds.length > 0`) -- a disabled
    // `useGetList` otherwise stays "pending" forever (same convention as
    // `AdvisorToday.tsx`'s own `isPending`, which likewise excludes its
    // enabled-gated queries), which would leave an advisor with zero open
    // leads stuck on the skeleton.
    isPending: isDealsPending || (dealIds.length > 0 && isAlertsPending),
    error,
    refetchAlerts,
    evaluateAndRefresh,
  };
}
